import React, { useState, useEffect } from "react";
import moment from "moment";
import axios, { CancelToken } from "axios";
import {
  ResultItemProps,
  HawkClient,
  useHawkConfig,
  SearchResponse,
  Result,
} from "react-hawksearch";
import {
  getField,
  padZerosToString,
  deserializeField,
  formatDate
} from "../../../../util/DynamicItemUtils";

import RegisteredFirms from "./RegisteredFirms";
import FormAPFileDetails from "./FormAPFilingDetails";
import ChinaBasedReferredWork from "./ChinaBasedReferredWork";
import DeniedIssuerAuditClient from "./DeniedIssuerAuditClient";

export default function FormAPFiling({ item, shouldToggle }: any) {
  // client setup for hawksearch api call
  const { config } = useHawkConfig();
  const client = new HawkClient(config);

  const urlSearchString = window.location.search;
  const urlParams = new URLSearchParams(urlSearchString);

  const [expandedFirms, setExpandedFirms] = useState(false);
  const [formData, setFormData] = useState([] as ResultItemProps[]);
  const [formDataLoaded, setFormDataLoaded] = useState(false);
  const [activeFormIndex, setActiveFormIndex] = useState(0);

  const dateParsingFormat = "YYYY-MM-DDTHH:mm:ssZ";
  const urlString = window.location.href;

  const issuerTicker = getField(item, "issuerticker");
  const issuerCikExists = !(getField(item, "issuerciknone") === "True");
  const issuerCik = getField(item, "issuercik");
  const otherParticpatingFirmInfo = deserializeField(
    item,
    "otherparticipantinfo"
  );
  const originalFirmFormId = getField(item, "originalfirmformid");

  let divclass = "";
  getField(item, "iscurrent") === "True" ? "" : (divclass = "superseded");

  useEffect(() => {
    if (shouldToggle !== expandedFirms) {
      setExpandedFirms(shouldToggle);
    }
  }, [shouldToggle])

  async function getFormFilingData(cancellationToken?: CancelToken) {
    let response: SearchResponse | null = null;
    let activeForm = 0;

    const formParams = {
      CustomUrl: "/resources/auditorsearch/form-ap-filing",
      ClientGuid: config.clientGuid,
      FacetSelections: {
        originalfirmformid: [originalFirmFormId],
      },
      IndexName: "",
    };

    try {
      response = await client.search(formParams, cancellationToken);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      console.error("Search request error:", error);
    }

    if (response && response.Results && response.Results.length > 1) {
      const orderedResults = [...response.Results]
        .sort(({ Document: documentA }, { Document: documentB }) => {
          if (
            documentA.filingdate &&
            documentA.filingdate.length > 0 &&
            documentB.filingdate &&
            documentB.filingdate.length > 0
          ) {
            if (
              moment(documentA.filingdate[0]).isAfter(documentB.filingdate[0])
            ) {
              return -1;
            } else if (
              moment(documentA.filingdate[0]).isBefore(documentB.filingdate[0])
            ) {
              return 1;
            } else {
              return 0;
            }
          } else {
            return 0;
          }
        })
        .map((doc, i) => {
          if (
            doc.Document &&
            doc.Document.filingid &&
            doc.Document.filingid.length > 0
          ) {
            if (doc.Document.filingid[0] === urlParams.get("filingid")) {
              activeForm = i;
            }
          }
          return new Result(doc);
        });

      setFormDataLoaded(true);
      setFormData(orderedResults);
      setActiveFormIndex(activeForm);
    }
  }

  function formatIssuerTickerandCik() {
    let tickerCikString = "";
    if (issuerTicker && issuerCikExists) {
      tickerCikString = `(${issuerTicker} | ${padZerosToString(issuerCik)})`;
    }
    if (issuerTicker && !issuerCikExists) {
      tickerCikString = `(${issuerTicker})`;
    }
    if (!issuerTicker && issuerCikExists) {
      tickerCikString = `(${padZerosToString(issuerCik)})`;
    }
    return tickerCikString;
  }

  function populateNewFormData(index) {
    urlParams.set("filingid", getField(formData[index], "filingid"));
    const newParams = urlParams.toString();
    window.history.replaceState(
      null,
      "",
      `/resources/auditorsearch/form-ap-filing?${newParams}`
    );
    setActiveFormIndex(index);
  }

  let page;
  switch (true) {
    case urlString.includes("filingid="):
      page = null;
      if (!formDataLoaded) {
        getFormFilingData();
      }
      if (formData.length > 1) {
        page = (
          <div>
            <ul className="form-tab-nav">
              {formData.map((data, i) => {
                const isCurrent = i === 0;
                const isLast = i === formData.length - 1;
                return (
                  <li key={`form-details_${i}`}>
                    <button
                      className={`form-tab-nav__tab${
                        activeFormIndex === i ? " active" : ""
                      }`}
                      onClick={() => populateNewFormData(i)}
                    >
                      <div>
                        <strong>
                          {moment(
                            getField(data, "filingdate"),
                            dateParsingFormat
                          ).format(formatDate(getField(data, "filingdate")))}
                        </strong>
                      </div>
                      <div>{`Form AP${!isLast ? "/A" : ""}`}</div>
                      <div>
                        <em>
                          {isCurrent ? (
                            <strong>(current)</strong>
                          ) : (
                            "(superceded)"
                          )}
                        </em>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <FormAPFileDetails item={formData[activeFormIndex]} />
          </div>
        );
      }
      if (formData.length === 1) {
        page = <FormAPFileDetails item={formData[activeFormIndex]} />;
      }
      if (formData.length < 1) {
        page = <FormAPFileDetails item={item} />;
      }
      break;

    case urlString.includes("/registered-firms"):
      page = <RegisteredFirms item={item} />;
      break;

    case urlString.includes("/china-referred-work"):
      page = <ChinaBasedReferredWork item={item} />;
      break;

    case urlString.includes("/denied-access-to-inspections"):
      page = <DeniedIssuerAuditClient item={item} />;
      break;

    default:
      page = (
        <div className={divclass}>
          <div className="hawk-row">
            <div className="hawk-column">
              <div className="lead-text-lt">
                Form AP Filing <span className="iscurrent">{divclass}</span>
              </div>
              <div className="lead-text-st">
                <a
                  href={`/resources/auditorsearch/form-ap-filing?filingid=${getField(
                    item,
                    "filingid"
                  )}&originalfirmformid=${getField(
                    item,
                    "originalfirmformid"
                  )}`}
                >
                  {moment(
                    getField(item, "filingdate"),
                    dateParsingFormat
                  ).format(formatDate(getField(item, "filingdate")))}
                </a>
              </div>
            </div>
            <div className="hawk-column">
              <div className="lead-text-lt">Year</div>
              <div className="lead-text-st">
                {getField(item, "filingaudityear") &&
                  getField(item, "filingaudityear")
                    .replace("+000000000000", "")
                    .replace(".0000", "")}
              </div>
            </div>
            <div className="hawk-column">
              <div className="lead-text-lt">Engagement Partner</div>
              <div className="lead-text-st">
                <a
                  href={
                    "/resources/auditorsearch/engagement-partners/?engagementpartnermasterid=" +
                    getField(item, "engagementpartnermasterid")
                  }
                >
                  {getField(item, "engagementpartnername")}
                </a>
              </div>
            </div>
            <div className="hawk-column">
              <div className="lead-text-lt">Firm</div>
              <div className="lead-text-st">
                <a
                  className="break-word"
                  href={
                    "/resources/auditorsearch/firms/?firmid=" +
                    getField(item, "firmid")
                  }
                >
                  {getField(item, "firmname")} ({getField(item, "firmid")})
                </a>
              </div>
            </div>
            <div className="hawk-column">
              <div className="lead-text-lt">Issuer</div>
              <div className="lead-text-st">
                {getField(item, "issuerid") && issuerCikExists ? (
                  <a
                    href={
                      "/resources/auditorsearch/issuers/?issuerid=" +
                      getField(item, "issuerid") +
                      "&issuercik=" +
                      getField(item, "issuercik")
                    }
                  >
                    {`${getField(
                      item,
                      "issuername"
                    )} ${formatIssuerTickerandCik()}`}
                  </a>
                ) : (
                  <div>{`${getField(
                    item,
                    "issuername"
                  )} ${formatIssuerTickerandCik()}`}</div>
                )}
              </div>
            </div>
            {otherParticpatingFirmInfo ? (
              <div className="hawk-modal-link-plus-container">
                <button
                  className={`hawk-modal-link-plus${
                    expandedFirms ? " hawk-modal-link-plus_minus" : ""
                  }`}
                  onClick={() => setExpandedFirms(!expandedFirms)}
                >
                  Other Participating Firms
                </button>
                <div className="modal-position">
                  <ul
                    className={`participant-modal${
                      expandedFirms ? " expand" : ""
                    }`}
                  >
                    {otherParticpatingFirmInfo[0].map((f) => {
                      let firmElement;
                      if (f.FirmID) {
                        firmElement = (
                          <li>
                            <a
                              className="break-word"
                              href={`/resources/auditorsearch/firms/?firmid=${f.FirmID}`}
                            >{`${f.FirmName} (${f.FirmID})`}</a>
                          </li>
                        );
                      } else {
                        firmElement = (
                          <li className="no-link break-word">{f.FirmName}</li>
                        );
                      }
                      return firmElement;
                    })}
                  </ul>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );
      break;
  }

  return page;
}
